import {
  Box,
  IconButton,
  Stack,
  Typography,
  useResponsive,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  SwipeableDrawer
} from '@rabex-kit/rabex-ui';
import React, { ReactNode, useEffect, useState } from 'react';
import CloseIcon from '../../static/images/svg/Close.svg?react';
import { ABOVE_MODAL_Z_INDEX, MODAL_Z_INDEX } from '../../constants/layout';
import { useBackButtonClose } from '../../hooks/useBackButtonClose';
import { CustomModalProps } from './types';

type ModalProps = Omit<
  CustomModalProps,
  | 'title'
  | 'hasTitleBorder'
  | 'body'
  | 'actions'
  | 'icon'
  | 'hasTitleBar'
  | 'footer'
  | 'mountBodyOnOpen'
> & {
  children: ReactNode;
  /** Called once the close animation has finished. */
  onExited?: () => void;
};
const Modal = ({
  children,
  paperProps,
  isOpen,
  onClose,
  footerExist,
  maxWidth,
  width,
  stacked,
  disableSwipeToClose,
  onExited
}: ModalProps) => {
  const { matches } = useResponsive();
  const { sx: paperSx, ...otherPaperProps } = paperProps || {};

  // Android back button dismisses the modal instead of navigating away from the page.
  useBackButtonClose(isOpen, onClose);

  // A sheet opened from another sheet has to clear its parent's layer.
  let sheetZIndex = footerExist ? 10000 : MODAL_Z_INDEX;
  if (stacked) sheetZIndex = ABOVE_MODAL_Z_INDEX;

  const paperSxMobile = {
    borderRadius: 3,
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
    outline: 'none',
    ...paperSx
  };

  return matches({
    xs: disableSwipeToClose ? (
      // A plain drawer: same sheet, but no drag-to-close.
      <Drawer
        anchor="bottom"
        open={isOpen}
        onClose={onClose}
        SlideProps={{ onExited }}
        PaperProps={{ sx: paperSxMobile }}
        sx={{ zIndex: sheetZIndex }}>
        {children}
      </Drawer>
    ) : (
      <SwipeableDrawer
        keepMounted
        anchor="bottom"
        open={isOpen}
        onOpen={() => null}
        onClose={onClose}
        swipeAreaWidth={0}
        disableSwipeToOpen={false}
        SlideProps={{ onExited }}
        PaperProps={{ sx: paperSxMobile }}
        sx={{ zIndex: sheetZIndex }}>
        {children}
      </SwipeableDrawer>
    ),
    lg: (
      <Dialog
        disableScrollLock
        fullWidth
        maxWidth={maxWidth}
        PaperProps={{
          elevation: 0,
          ...otherPaperProps,
          sx: { borderRadius: 3, outline: 'none', width, ...paperSx }
        }}
        open={isOpen}
        onClose={onClose}
        TransitionProps={{ onExited }}>
        {children}
      </Dialog>
    )
  });
};

const CustomModal = ({
  title,
  hasTitleBorder = false,
  isOpen,
  onClose,
  body,
  actions,
  icon,
  paperProps,
  footerExist = false, // better to remove this footerExist at all (no need - never used)
  maxWidth = 'sm',
  width = 502,
  hasTitleBar = true,
  titleLeadingIcon,
  footer,
  stacked = false,
  disableSwipeToClose = false,
  onExited,
  mountBodyOnOpen = false
}: CustomModalProps) => {
  const { matches } = useResponsive();
  const theme = useTheme();
  // Stays true through the close animation, so the sheet does not empty out
  // while it slides away.
  const [isBodyMounted, setBodyMounted] = useState(isOpen);
  useEffect(() => {
    if (isOpen) setBodyMounted(true);
  }, [isOpen]);
  const showBody = !mountBodyOnOpen || isOpen || isBodyMounted;

  return (
    <Modal
      isOpen={isOpen}
      paperProps={paperProps}
      footerExist={footerExist}
      stacked={stacked}
      disableSwipeToClose={disableSwipeToClose}
      onExited={() => {
        if (mountBodyOnOpen) setBodyMounted(false);
        onExited?.();
      }}
      maxWidth={maxWidth}
      onClose={onClose}
      width={width}>
      <>
        {hasTitleBar && (
          <DialogTitle
            sx={{
              borderBottom: hasTitleBorder
                ? `1px solid ${theme.palette.base[200]}`
                : 'unset'
            }}>
            <Stack alignItems="center" justifyContent="center">
              <Box textAlign="center">{icon && icon}</Box>
              <Stack
                width="100%"
                direction="row"
                alignItems="center"
                justifyContent="space-between">
                {titleLeadingIcon && titleLeadingIcon}
                <Typography
                  flex={1}
                  variant={matches({ xs: 'body1', lg: 'body1' })}
                  weight="semiBold"
                  textAlign="start"
                  color={theme.palette.textColor[900]}>
                  {title}
                </Typography>
                <IconButton
                  onClick={onClose}
                  size="sm"
                  sx={{
                    color: theme.palette.base[950]
                    // theme.palette.mode === 'light'
                    //   ? theme.palette.base[950]
                    //   : theme.palette.grayBackground.A100,
                    // padding: 1
                  }}>
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Stack>
          </DialogTitle>
        )}
        <DialogContent
          sx={{
            marginTop: hasTitleBorder ? 4 : hasTitleBar ? 2 : 0,
            padding: matches({ xs: 4, lg: 4 }),
            paddingBottom: actions ? 0 : matches({ xs: 4, lg: 6 })
          }}>
          {showBody && body}
        </DialogContent>
        {footer && (
          <Box
            sx={{
              borderTop: `1px solid ${theme.palette.base[200]}`,
              position: 'sticky',
              bottom: 0,
              // backgroundColor: theme.palette.base[50],
              zIndex: 1
            }}>
            {footer}
          </Box>
        )}
        {actions && (
          <DialogActions
            sx={{
              // paddingTop: matches({ xs: 3, md: 4 }),
              padding: matches({ xs: 4, lg: 6 }),
              justifyContent: 'center'
            }}>
            {actions}
          </DialogActions>
        )}
      </>
    </Modal>
  );
};

export default CustomModal;
